import type {
  WorkspaceRoleKey} from 'hooks/globalApi';
import assert from 'assert';
import { useToast } from 'components/ToastManager';
import {
  useCreateUserWorkspaceInviteMutation,
  useDeleteUserWorkspaceInviteMutation,
  useGetPendingUserWorkspaceInvitesQuery,
  useGetWorkspaceInviteByIdQuery,
  useGetWorkspaceMembersQuery as useGetWorkspaceMembersQueryGlobal
} from 'hooks/globalApi';
import { useGlobalApiContext } from 'hooks/useGlobalApiContext';
import { useHasWorkspacePermission } from 'hooks/useHasWorkspacePermission';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getResultOrError } from 'utils/errors';

import { Permissions } from '@fragment/authorization-utils/permissions';
import lilId from '@fragment/lil-id';
import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { TextButton } from '@fragment/ui/src/components/Button/TextButton/TextButton';
import { Icon } from '@fragment/ui/src/components/Icon';
import { Table } from '@fragment/ui/src/components/Table';
import { Toast } from '@fragment/ui/src/components/Toast/Toast';

import { workspaceRoleLabels } from '../../../../utils/workspaceRoles';
import { useWorkspaceId } from '../../../../wrappers/WorkspaceProvider';
import { MemberDetail } from '../../../settings/MemberDetail';
import { TableLoadError } from '../../../TableLoadError/TableLoadError';
import { InviteMember } from './InviteMember';

type MemberRow = {
  id: string;
  email: string;
  created: string;
  role: WorkspaceRoleKey;
  firstName?: string | null;
  lastName?: string | null;
};

type InviteRow = {
  id: string;
  email: string;
  resend?: () => void;
  delete?: () => void;
};

const membersSchema = [
  {
    title: 'Members',
    key: 'email',
  },
  {
    title: 'Permissions',
    format: (value: MemberRow) => <span>{workspaceRoleLabels[value.role]}</span>,
    justify: 'right',
  },
] as const;

const invitesSchema = [
  {
    title: 'Invites',
    colspan: 'col-span-3',
    key: 'email',
  },
  {
    title: 'Actions',
    format: (value: InviteRow) => (
      <div className="flex items-center justify-end gap-x-[0ch]">
        {value.resend && (
          <TextButton
            onClick={() => {
              value.resend?.();
            }}
            tooltipText="Resend invite"
            data-testid={`resend-invite-button-${value.email}`}
          >
            ↺
          </TextButton>
        )}
        {value.delete && (
          <TextButton
            onClick={() => {
              value.delete?.();
            }}
            icon={
              <div className="w-fit pb-[3px]">
                {/* pb is to center the icon */}
                <Icon type="trash" size="md" primaryColor="gray" />
              </div>
            }
            hoverIcon={
              <div className="w-fit pb-[3px]">
                <Icon type="trash" size="md" primaryColor="black" />
              </div>
            }
            tooltipText="Delete invite"
            data-testid={`delete-invite-button-${value.email}`}
          />
        )}
      </div>
    ),
    justify: 'right',
    colspan: 'col-span-1',
  },
] as const;

const readOnlyInvitesSchema = [
  {
    title: 'Invites',
    key: 'email',
  },
] as const;

const InvitesListTable = ({
  workspaceId,
  canManageInvites,
}: {
  workspaceId: string;
  canManageInvites: boolean;
}) => {
  const { context } = useGlobalApiContext();
  const { showToast } = useToast();
  const variables = useMemo(() => ({ workspaceId }), [workspaceId]);
  const [{ data, error, fetching }, reExecuteMembersQuery] =
    useGetPendingUserWorkspaceInvitesQuery({
      variables,
      context,
    });

  const { isError: isWorkspaceError, result: workspace } = getResultOrError(
    data?.workspace,
    error
  );

  const refreshInvitesTable = useCallback(
    () => reExecuteMembersQuery({ requestPolicy: 'network-only' }),
    [reExecuteMembersQuery]
  );

  // call back for deleting a user invite
  const [, deleteUserWorkspaceInvite] = useDeleteUserWorkspaceInviteMutation();
  const deleteInvite = useCallback(
    async (inviteId: string, shouldShowToast = true) => {
      const response = await deleteUserWorkspaceInvite(
        { workspaceId, inviteId },
        context
      );
      if (
        response.data?.deleteUserWorkspaceInvite.__typename ===
        'BadRequestError'
      ) {
        showToast(
          <Toast
            type="error"
            message="An error occurred while deleting the invite. Please try again."
          />
        );
        return false;
      }
      if (shouldShowToast) {
        showToast(
          <Toast type="success" message="Invite deleted" id={inviteId} />
        );
      }
      return true;
    },
    [deleteUserWorkspaceInvite, workspaceId, showToast, context]
  );

  // call back for resending a user invite (delete then create new with same email)
  const [, createUserWorkspaceInvite] = useCreateUserWorkspaceInviteMutation();
  const resendInvite = useCallback(
    async (inviteId: string, email: string, role: WorkspaceRoleKey) => {
      const newInviteId = lilId();
      const result = await createUserWorkspaceInvite(
        {
          workspaceId,
          invites: [
            {
              inviteId: newInviteId,
              email,
              role,
            },
          ],
        },
        context
      );
      if (
        result.data?.createUserWorkspaceInvite.__typename === 'BadRequestError'
      ) {
        showToast(
          <Toast
            type="error"
            message="An error occurred while resending the invite. Please try again or create a new one."
          />
        );
      } else {
        // delete the original invite if we successfully sent a new one
        const deleted = await deleteInvite(inviteId, false);
        if (deleted) {
          showToast(<Toast type="success" message="Invite resent" />);
        } else {
          // delete the new invite if we failed to delete the original one
          await deleteInvite(newInviteId, false);
          showToast(
            <Toast
              type="error"
              message="An error occurred while resending the invite. Please try again or create a new one."
            />
          );
        }
      }
    },
    [createUserWorkspaceInvite, workspaceId, context, deleteInvite, showToast]
  );

  if (isWorkspaceError) {
    return (
      <TableLoadError
        entityColumnName="Email"
        rightColumnName={canManageInvites ? 'Actions' : ''}
        notFoundErrorCode="member_not_found"
        {...{
          error,
          retry: refreshInvitesTable,
          fetching,
        }}
        data-testid="invites-list-table-error"
      />
    );
  }
  const InvitesRowData = workspace.pendingInvites.flatMap((invite) => {
    if (!invite.email || !invite.inviteId) {
      return [];
    }
    return [
      {
        id: invite.inviteId,
        email: invite.email,
        ...(canManageInvites
          ? {
              delete: async () => {
                await deleteInvite(invite.inviteId);
              },
              resend: async () => {
                await resendInvite(invite.inviteId, invite.email, invite.role);
              },
            }
          : {}),
      },
    ];
  });

  return (
    <Table
      textSize="md"
      data={InvitesRowData}
      schema={canManageInvites ? invitesSchema : readOnlyInvitesSchema}
      borderedRows
      emptyValue={
        canManageInvites
          ? { Invites: 'No Invites', Actions: 'N/A' }
          : { Invites: 'No Invites' }
      }
      data-testid="invites-list-table"
    />
  );
};

const MembersListTable = ({
  workspaceId,
  onRowClick,
}: {
  workspaceId: string;
  onRowClick: (row: MemberRow) => void;
}) => {
  const { context } = useGlobalApiContext();
  const variables = useMemo(() => ({ workspaceId }), [workspaceId]);
  const [{ data, error, fetching }, reExecuteMembersQuery] =
    useGetWorkspaceMembersQueryGlobal({
      variables,
      context,
    });

  const { isError: isWorkspaceError, result: workspace } = getResultOrError(
    data?.workspace,
    error
  );
  const { isError: isMembersError, result: members } = getResultOrError(
    workspace?.members,
    error
  );

  const memberRowData = isMembersError
    ? []
    : members.nodes.flatMap((member) => {
        if (member?.user.__typename !== 'User' || !member.user.email) {
          return [];
        }
        return [
          {
            id: member.user.id,
            email: member.user.email,
            created: member.created,
            role: member.role,
            firstName: member.user.firstName,
            lastName: member.user.lastName,
          },
        ];
      });

  const refreshMemberTable = useCallback(
    () => reExecuteMembersQuery({ requestPolicy: 'network-only' }),
    [reExecuteMembersQuery]
  );

  if (isWorkspaceError || isMembersError) {
    return (
      <TableLoadError
        entityColumnName="Email"
        rightColumnName="Permissions"
        notFoundErrorCode="member_not_found"
        {...{ error, retry: refreshMemberTable, fetching }}
        data-testid="members-list-table-error"
      />
    );
  }

  return (
    <Table
      textSize="md"
      data={memberRowData}
      schema={membersSchema}
      onRowClick={onRowClick}
      borderedRows
      data-testid="members-list-table"
    />
  );
};

export const MembersList = ({
  hideHeader = false,
  onAction,
}: {
  hideHeader?: boolean;
  onAction?: (
    action: { title: string; onClick: () => void } | undefined
  ) => void;
} = {}) => {
  const workspaceId = useWorkspaceId();
  const { context, token } = useGlobalApiContext();
  const variables = useMemo(() => ({ workspaceId }), [workspaceId]);
  const [{ data: workspaceData }] = useGetWorkspaceInviteByIdQuery({
    variables,
    context,
    pause: !token,
  });
  assert(workspaceData?.workspace.__typename === 'Workspace');
  const [clickedMember, setClickedMember] = useState<MemberRow>();
  const [showAddMemberView, setShowAddMemberView] = useState<boolean>(false);
  const canInvite = useHasWorkspacePermission(
    Permissions.workspace_member.invite
  );

  useEffect(() => {
    if (!onAction) return;
    onAction(
      canInvite && !clickedMember && !showAddMemberView
        ? { title: 'Invite +', onClick: () => setShowAddMemberView(true) }
        : undefined
    );
  }, [canInvite, clickedMember, showAddMemberView, onAction]);

  return (
    <div className="flex flex-col w-full h-full">
      {clickedMember ? (
        <>
          {!hideHeader && (
            <div className="flex justify-between h-fit">
              <div className="flex space-x-[1ch] items-center">
                <TextButton
                  onClick={() => {
                    setClickedMember(undefined);
                  }}
                  aria-label="Back"
                >
                  <Icon type="left" size="md" />
                </TextButton>
                <span>Profile</span>
              </div>
            </div>
          )}
          <div className="h-f4" />
          <MemberDetail
            row={clickedMember}
            onDelete={() => {
              setClickedMember(undefined);
            }}
          />
        </>
      ) : (
        <>
          {!hideHeader && (
            <div className="flex justify-between pb-f4">
              <span>{showAddMemberView ? 'Invite Members' : 'Members'}</span>
              {canInvite && !showAddMemberView && (
                <PrimaryButtonV2
                  onClick={() => {
                    setShowAddMemberView(true);
                  }}
                >
                  Invite<span className="text-main-500">+</span>
                </PrimaryButtonV2>
              )}
            </div>
          )}
          {showAddMemberView ? (
            <InviteMember setShowAddMemberView={setShowAddMemberView} />
          ) : (
            <div className="flex flex-col gap-f4">
              <InvitesListTable
                workspaceId={workspaceId}
                canManageInvites={canInvite}
              />
              <MembersListTable
                workspaceId={workspaceId}
                onRowClick={setClickedMember}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
